import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, HostBinding, OnInit, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { PaginationResult } from 'src/app/services/abstract/abstract.service';
import { CartOfferRepresentation, CartOfferService } from 'src/app/services/cart-offer/cart-offer.service';
import { CartService } from 'src/app/services/cart/cart.service';
import { ProductService } from 'src/app/services/product/product.service';
import { CurrencyAndLanguageService } from 'src/app/shared/components/currency/currency-and-language.service';
import { Cart, CartOffer as VLCartOffer, Product, Rental } from 'src/app/models/verylys';
import { CartService as CartComponentService } from './cart.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { VgOffCanvasComponent } from 'src/app/shared/components/vg-off-canvas/vg-off-canvas.component';
import { StepOrderDetailsService } from 'src/app/pages/cart/step-order-details/step-order-details.service';
import { ProductPageOrderService } from 'src/app/pages/product/order/order.service';
import { VgPopupAlertService } from 'src/app/shared/components/vg-popup-alert/vg-popup-alert.service';
import { POPUP_ALERT_TYPE } from 'src/app/shared/components/vg-popup-alert/vg-popup-alert';
import { clone } from 'lodash';
import { KeycloakInitializerService } from 'src/app/services/keycloakInitializer.service';
import { RentalService } from 'src/app/services/rental/rental.service';
import { AuthService } from 'src/app/services/auth.service';

interface CartOffer extends VLCartOffer {
    representation: CartOfferRepresentation;
    renewal?: {
        friendlyId: string;
        customName: string;
    }
}

@Component({
    selector: 'cart',
    templateUrl: './cart.component.html',
    styleUrls: ['./cart.component.scss']
})
export class CartComponent implements OnInit {
    @ViewChild('offCanvasCart') private offCanvasCart: VgOffCanvasComponent;

    @HostBinding('class') class: string;

    public cart: Cart = null;
    public flushing: boolean = false;
    private products: Product[] = [];
    protected pendingDeletionsIds: string[] = [];
    protected deletedCartOffers: string[] = [];
    protected cartRouting: boolean = false;
    private _destroy$: Subject<void> = new Subject();
    private rentals: Rental[] = [];

    constructor(private cartService: CartService,
        private cartOfferService: CartOfferService,
        private cartComponentService: CartComponentService,
        private currencyAndLanguageService: CurrencyAndLanguageService,
        private productService: ProductService,
        private router: Router,
        private route: ActivatedRoute,
        private productPageOrderService: ProductPageOrderService,
        private stepOrderDetailsService: StepOrderDetailsService,
        private popupAlertService: VgPopupAlertService,
        private keycloakInitializer: KeycloakInitializerService,
        private rentalService: RentalService,
        private authService: AuthService) { }

    async ngOnInit() {
        this.loadRentals();

        this.currencyAndLanguageService.onCurrencyChange
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
                this.cart = null;
                this.loadCart()
            })

        this.productPageOrderService.onValidate
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
                this.cart = null;
                this.loadCart()
            })

        /*this.stepOrderDetailsService.onReload
          .pipe(takeUntil(this._destroy$))
          .subscribe(async () => this.loadCart());*/

        this.cartComponentService.onReload
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
                this.cart = null;
                this.loadCart()
            })

        if (!this.products?.length) {
            this.productService.getDetailedList()
                .pipe(takeUntil(this._destroy$))
                .subscribe(async (result: PaginationResult<Product>) => {
                    this.products = result?.results;
                    this.loadCart();
                });
        } else {
            this.loadCart();
        }

        this.router.events
            .pipe(takeUntil(this._destroy$))
            .subscribe(event => {
                if (event instanceof NavigationEnd) {
                    this.cartRouting = event.url.includes('/cart');
                }
            });
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    protected checkout(): void {
        this.offCanvasCart?.close();
        this.router.navigate(['/cart']);
    }

    protected flush() {
        if (this.flushing) {
            return;
        }

        const previousCart: Cart = clone(this.cart);
        this.cart = null;

        this.cartService.flush()
            .subscribe({
                next: () => {
                    this.stepOrderDetailsService.reload();
                    this.cart = {} as Cart;
                },
                error: (error: HttpErrorResponse) => {
                    this.popupAlertService.open(POPUP_ALERT_TYPE.DANGER, $localize`:@@header.unable_clear:Unable to clear the cart, please contact support.`);
                    this.cart = previousCart;
                }
            });
    }

    protected async deleteCartOffer(cartOffer: CartOffer): Promise<void> {
        const cartOfferId: string = cartOffer._id;
        if (this.isDeletedCartOffer(cartOfferId)) {
            return;
        }

        this.pendingDeletionsIds.push(cartOfferId);

        try {
            await this.cartOfferService.removeFromCart(cartOffer).toPromise();
            //await new Promise((resolve, reject) => setTimeout(() => resolve(""), 3000));

            this.deletedCartOffers.push(cartOfferId);

            const index: number = (this.cart.cart_offers as CartOffer[]).findIndex((value) => value._id == cartOfferId);
            (this.cart.cart_offers as CartOffer[]).splice(index, 1);

            //this.cartComponentService.reload();
        } catch (error) {
            // TODO: SENTRY
        } finally {
            const index: number = this.pendingDeletionsIds.indexOf(cartOffer._id);
            this.pendingDeletionsIds.splice(index, 1);

            if (this.pendingDeletionsIds.length == 0) {
                this.stepOrderDetailsService.reload();
                this.loadCart();
            }
        }
    }

    protected isDeletedCartOffer(cartOfferId: string): boolean {
        return this.pendingDeletionsIds.includes(cartOfferId) || this.deletedCartOffers.includes(cartOfferId);
    }

    private async loadingcCart(): Promise<void> {
        this.deletedCartOffers = [];

        this.cartService.getOrCreateMyCart()
            .pipe(takeUntil(this._destroy$))
            .subscribe({
                next: async (result: Cart) => {
                    this.cart = result;
                    if (!this.cart) {
                        return;
                    }

                    const cartOffers: CartOffer[] = this.cart.cart_offers as CartOffer[];

                    // Get representation 
                    const representations: Promise<CartOfferRepresentation>[] = [];
                    cartOffers?.map((cartOffer: CartOffer) => representations.push(this.cartOfferService.getCartOfferRepresentation(cartOffer, { image: true }, this.products)));

                    const representationResult = await Promise.all(representations);
                    representationResult.forEach((result: CartOfferRepresentation, index: number) => (this.cart.cart_offers as CartOffer[])[index].representation = result);

                    this.setRentalRenewInformations();
                },
                error: (error: HttpErrorResponse) => {
                    this.cart = null;
                    // TODO: sentry ?
                }
            });
    }

    /**
     * Load the cart
     */
    private async loadCart(): Promise<void> {
        if (this.keycloakInitializer.isInitialized) {
            this.loadingcCart();
        } else {
            this.keycloakInitializer.initialized
                .pipe(take(1), takeUntil(this._destroy$))
                .subscribe(() => {
                    this.loadingcCart();
                    this.setRentalRenewInformations();
                })
        }
    }

    private loadRentals() {
        if (this.keycloakInitializer.isInitialized) {
            this.loadingRentals();
        } else {
            this.keycloakInitializer.initialized
                .pipe(take(1), takeUntil(this._destroy$))
                .subscribe(() => {
                    this.loadingRentals();
                    this.setRentalRenewInformations();
                })
        }
    }

    private async loadingRentals() {
        const loggedIn: boolean = await this.authService.isLoggedIn();
        if (!loggedIn) {
            return;
        }

        this.rentalService.getAll()
            .pipe(takeUntil(this._destroy$))
            .subscribe((result: PaginationResult<Rental>) => {
                this.rentals = result.results;
                this.setRentalRenewInformations();
            });
    }

    private setRentalRenewInformations() {
        if (!this.cart?.cart_offers?.length) {
            return;
        }

        if (!this.rentals?.length) {
            return;
        }

        for (let cartOffer of this.cart.cart_offers as CartOffer[]) {
            if (!cartOffer.rental) {
                continue;
            }

            let rental: Rental = this.rentals.find((item: Rental) => item._id == cartOffer.rental);
            if (!rental) {
                continue;
            }

            cartOffer.renewal = {
                friendlyId: rental.friendly_id,
                customName: rental.parameters?.custom_name?.value
            }
        }
    }
}